import React, { memo, useCallback } from 'react';
import { useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { UserRole } from 'context';
import { LoadingIndicator, ToastTypes } from 'atoms';
import {
  authState,
  useHomeworkDownloadUrl,
  useLessonHomeworkAssignment,
  useMyLessonHomework,
  useSubmitHomework,
  useToaster,
} from 'core';

import { HomeworkSubmitComponent } from './HomeworkSubmit';
import { HomeworkAssignmentCard } from './HomeworkAssignmentCard';

interface IHomeworkSubmitContainer {
  lessonId: string;
}

const StudentHomework = memo(({ lessonId }: IHomeworkSubmitContainer) => {
  const { t } = useTranslation();
  const { addToast } = useToaster();
  const { homework, loading, error } = useMyLessonHomework(lessonId);
  const { submitHomework, loading: submitLoading } = useSubmitHomework();
  const { getDownloadUrl, loading: downloadLoading } = useHomeworkDownloadUrl();

  /**
   * The student re-downloading their OWN ZIP. The backend resolver already
   * allows this ("students get their own file only") — the hook was simply
   * never wired on this side, so a student could not check what they had sent.
   * Popup handling mirrors the diplomas download: open the tab synchronously
   * inside the click (Safari vetoes a post-await open), disown it, and say so
   * when the browser blocks it anyway instead of looking like a no-op.
   */
  const onDownload = useCallback(async () => {
    const submissionId = homework?.mySubmission?.id;
    if (!submissionId) {
      return;
    }
    const tab = window.open('', '_blank');
    if (tab) {
      tab.opener = null;
    }
    try {
      const url = await getDownloadUrl(submissionId);
      if (!url) {
        tab?.close();
        addToast('homework_download', {
          type: ToastTypes.danger,
          text: `${t('homework.submit.downloadError')}`,
        });
        return;
      }
      if (tab) {
        tab.location.href = url;
      } else {
        addToast('homework_download', {
          type: ToastTypes.warning,
          text: `${t('homework.submit.popupBlocked')}`,
        });
      }
    } catch {
      tab?.close();
      addToast('homework_download', {
        type: ToastTypes.danger,
        text: `${t('homework.submit.downloadError')}`,
      });
    }
  }, [homework?.mySubmission?.id, getDownloadUrl, addToast, t]);

  const onSubmit = useCallback(
    async (source: { githubUrl?: string; file?: File }) => {
      const result = await submitHomework(lessonId, source);
      if (result.success) {
        addToast('homework_submit', {
          type: ToastTypes.success,
          text: `${t('homework.submit.success')}`,
        });
      }
      return result;
    },
    [submitHomework, lessonId, addToast, t],
  );

  if (loading) {
    return <LoadingIndicator />;
  }

  if (error) {
    return <EmptyText>{t('homework.loadError')}</EmptyText>;
  }

  if (!homework) {
    return <EmptyText>{t('homework.assignment.noAssignment')}</EmptyText>;
  }

  return (
    <HomeworkSubmitComponent
      homework={homework}
      submitLoading={submitLoading}
      onSubmit={onSubmit}
      onDownload={onDownload}
      downloadLoading={downloadLoading}
    />
  );
});

const StaffHomework = memo(({ lessonId }: IHomeworkSubmitContainer) => {
  const { t } = useTranslation();
  const { assignment, loading, error } = useLessonHomeworkAssignment(lessonId);

  if (loading) {
    return <LoadingIndicator />;
  }

  if (error) {
    return <EmptyText>{t('homework.loadError')}</EmptyText>;
  }

  if (!assignment) {
    return <EmptyText>{t('homework.assignment.noAssignment')}</EmptyText>;
  }

  return (
    <StaffWrapper>
      <HomeworkAssignmentCard assignment={assignment} />
    </StaffWrapper>
  );
});

/**
 * Role dispatch for the lesson "Домашнее задание" tab (spec 0012 §6):
 *   STUDENT              → assignment + submit form / submitted state;
 *   METHODIST (default)  → read-only assignment view. The teacher works
 *                          through the existing "Оценка заданий" flow and has
 *                          no tab pointing here, but this view stays correct
 *                          for a teacher navigating by URL.
 */
export const HomeworkSubmitContainer = memo(({ lessonId }: IHomeworkSubmitContainer) => {
  const { active_role } = useReactiveVar(authState);

  if (active_role === UserRole.STUDENT) {
    return <StudentHomework lessonId={lessonId} />;
  }

  return <StaffHomework lessonId={lessonId} />;
});

const EmptyText = styled.p`
  margin: 0;
  ${fontService.text({ type: TextType.normal, weight: FontWeight.regular })}
`;

const StaffWrapper = styled.div`
  max-width: 700px;
`;
